/**
 * ClipperInboxDebugCard — Admin Diagnostics (2026-02).
 *
 * READ-ONLY runtime diagnostic for the browser-extension "Staging"
 * inbox. Hits `GET /api/admin/diagnostics/clipper-inbox` and shows,
 * side-by-side, every value the read-side filter cares about:
 *
 *   1. CALLER IDENTITY   — user_id, company_id, email, role
 *      + BUILD FINGERPRINT (which pod build served this request)
 *   2. OWNER-SHAPE COUNTS across db.unsorted_items:
 *      total, matches_caller, null_user_id, null_company_id, null_both
 *   3. 25 MOST RECENT ROWS where company_id is null OR equals the
 *      caller's company — newest first.
 *
 * Deliberately runs against whatever `/api/...` the browser can reach,
 * so opening the card in production hits production Mongo.
 *
 * No writes. No migration. Super-admin only (backend 404s
 * non-super-admins). Only two controls: Re-run + Copy JSON.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Loader2, Inbox, Copy, RefreshCw, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function Row({ label, value, mono = true, testId }) {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="flex items-baseline gap-3 py-1" data-testid={testId}>
      <div className="text-[11px] text-slate-500 min-w-[160px] font-semibold uppercase tracking-wide">{label}</div>
      <div className={`text-sm text-slate-900 break-all ${mono ? 'font-mono' : ''}`}>
        {isEmpty ? <span className="italic text-slate-400">(empty)</span> : String(value)}
      </div>
    </div>
  );
}

function CountTile({ label, value, tone = 'slate', testId }) {
  const toneClass = {
    slate:   'bg-slate-50   border-slate-200   text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50   border-amber-200   text-amber-900',
    rose:    'bg-rose-50    border-rose-200    text-rose-900',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`p-2 rounded border ${toneClass}`} data-testid={testId}>
      <div className="text-[10px] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-lg font-mono font-semibold">{value}</div>
    </div>
  );
}

function RecentRowsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic px-2 py-2" data-testid="clipper-inbox-recent-empty">
        (no matching rows)
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border rounded overflow-hidden" data-testid="clipper-inbox-recent-table">
        <thead className="bg-slate-100">
          <tr className="text-left">
            <th className="p-2">created_at</th>
            <th className="p-2">item_type</th>
            <th className="p-2">title</th>
            <th className="p-2">user_id</th>
            <th className="p-2">company_id</th>
            <th className="p-2 text-center">sorted</th>
            <th className="p-2">source_url</th>
            <th className="p-2">id</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t hover:bg-slate-50" data-testid={`clipper-inbox-recent-row-${i}`}>
              <td className="p-2 font-mono whitespace-nowrap">{r.created_at || ''}</td>
              <td className="p-2">{r.item_type || ''}</td>
              <td className="p-2 max-w-[220px] truncate" title={r.title || ''}>
                {r.title || <span className="italic text-slate-400">(untitled)</span>}
              </td>
              <td className="p-2 font-mono max-w-[200px] truncate" title={r.user_id || ''}>
                {r.user_id
                  ? r.user_id
                  : <span className="italic text-rose-700">null</span>}
              </td>
              <td className="p-2 font-mono max-w-[200px] truncate" title={r.company_id || ''}>
                {r.company_id
                  ? r.company_id
                  : <span className="italic text-rose-700">null</span>}
              </td>
              <td className="p-2 text-center">{r.sorted ? '✓' : '—'}</td>
              <td className="p-2 max-w-[220px] truncate" title={r.source_url || ''}>
                {r.source_url || <span className="italic text-slate-400">(none)</span>}
              </td>
              <td className="p-2 font-mono max-w-[160px] truncate" title={r.id || ''}>{r.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ClipperInboxDebugCard({ isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/clipper-inbox`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const copyJSON = () => {
    if (!data) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied JSON');
    } catch { /* ignore */ }
  };

  if (!isSuperAdmin) return null;

  const caller = data?.caller;
  const totals = data?.unsorted_totals;
  const fp = data?.build_fingerprint;

  return (
    <div
      className="p-5 rounded-xl border border-slate-300 bg-white mb-6"
      data-testid="clipper-inbox-debug-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <Inbox className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Clipper Inbox (Debug)</h3>
          <p className="text-sm text-slate-700">
            Read-only. Reports what the WorkTable &quot;Staging&quot; panel would show for
            <em> the currently signed-in caller</em> against this pod&apos;s MongoDB, plus every
            owner-shape count on <code className="font-mono">db.unsorted_items</code>. Opening
            this card in production reports on production data. No writes, no migration.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={run}
            disabled={busy}
            data-testid="clipper-inbox-debug-run-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running…</>
            ) : data ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>
            ) : (
              <><Inbox className="w-4 h-4 mr-2" /> Run Clipper Inbox Debug</>
            )}
          </Button>
          {data && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyJSON}
              data-testid="clipper-inbox-debug-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copy JSON
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div
          className="mb-3 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2"
          data-testid="clipper-inbox-debug-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Diagnostic failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* 1. CALLER + BUILD FINGERPRINT */}
          <section data-testid="clipper-inbox-caller">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">1. Caller identity &amp; build</h4>
            <div className="rounded border bg-slate-50 px-3 py-2">
              <Row label="user_id"          value={caller.user_id}          testId="cid-user-id" />
              <Row label="company_id"       value={caller.company_id}       testId="cid-company-id" />
              <Row label="email"            value={caller.email}            testId="cid-email" mono={false} />
              <Row label="role"             value={caller.role}             testId="cid-role" mono={false} />
              <Row label="is_super_admin"   value={String(caller.is_super_admin)}   testId="cid-is-super-admin" />
              <Row label="is_company_admin" value={String(caller.is_company_admin)} testId="cid-is-company-admin" />
              <Row label="build_fingerprint" value={fp} testId="cid-build-fingerprint" />
            </div>
          </section>

          {/* 2. OWNER-SHAPE COUNTS */}
          <section data-testid="clipper-inbox-counts">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">
              2. <code className="font-mono">db.unsorted_items</code> owner-shape counts
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <CountTile
                label="Total rows"
                value={totals.total_rows}
                tone="slate"
                testId="clipper-inbox-count-total"
              />
              <CountTile
                label="Matches caller"
                value={totals.matches_caller}
                tone={totals.matches_caller > 0 ? 'emerald' : 'amber'}
                testId="clipper-inbox-count-matches"
              />
              <CountTile
                label="null user_id"
                value={totals.null_user_id}
                tone={totals.null_user_id > 0 ? 'amber' : 'slate'}
                testId="clipper-inbox-count-null-user"
              />
              <CountTile
                label="null company_id"
                value={totals.null_company_id}
                tone={totals.null_company_id > 0 ? 'amber' : 'slate'}
                testId="clipper-inbox-count-null-company"
              />
              <CountTile
                label="null on both"
                value={totals.null_both}
                tone={totals.null_both > 0 ? 'rose' : 'slate'}
                testId="clipper-inbox-count-null-both"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              &quot;Matches caller&quot; uses the same filter the WorkTable Staging panel does:
              <code className="font-mono ml-1">
                user_id == you AND company_id == you AND sorted != true
              </code>.
            </p>
          </section>

          {/* 3. RECENT ROWS */}
          <section data-testid="clipper-inbox-recent">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">
              3. 25 most recent rows <span className="text-xs text-slate-500 font-normal">
                (company_id is null OR equals the caller&apos;s company, newest first)
              </span>
            </h4>
            <RecentRowsTable rows={data.recent_rows || []} />
          </section>
        </div>
      )}
    </div>
  );
}
